import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AnnouncementBar from '../../components/feature/AnnouncementBar';
import { Vehicle } from '../../data/vehicles';
import { fetchAllVehicles } from '../../lib/vehicleService';
import { PaymentCalculator } from '../../components/feature/PaymentCalculator';
import ImportExcelModal from './components/ImportExcelModal';

const brands = ['All Brands', 'BMW', 'Chevrolet', 'Dodge', 'Ford', 'GMC', 'Honda', 'Hyundai', 'Infiniti', 'Jeep', 'Kia', 'Land Rover', 'Mazda', 'Mercedes-Benz', 'Mitsubishi', 'Nissan', 'Ram', 'Tesla', 'Toyota'];
const types = ['All Types', 'Sedan', 'SUV', 'Coupe', 'Convertible', 'Truck', 'Van'];
const fuels = ['All Fuel', 'Gas', 'Electric', 'Diesel', 'Hybrid'];
const conditions = ['All', 'New', 'Pre-Owned'];

const VEHICLES_PER_PAGE = 15;

export default function InventoryPage() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [selectedBrand, setSelectedBrand] = useState('All Brands');
  const [selectedModel, setSelectedModel] = useState('All Models');
  const [selectedType, setSelectedType] = useState('All Types');
  const [selectedFuel, setSelectedFuel] = useState('All Fuel');
  const [selectedCondition, setSelectedCondition] = useState('All');
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(0);
  const [yearMin, setYearMin] = useState(0);
  const [yearMax, setYearMax] = useState(0);
  const [mileageMin, setMileageMin] = useState(0);
  const [mileageMax, setMileageMax] = useState(0);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [sortBy, setSortBy] = useState('price-low');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [shareCopied, setShareCopied] = useState(false);

  const isEditMode = (() => {
    try { return window.self !== window.top; } catch { return true; }
  })();

  const [openSections, setOpenSections] = useState({
    brand: false, model: false, type: true, condition: true, fuel: true, price: true, year: true, mileage: true,
  });

  const toggleSection = (key: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Fetch from Supabase ──────────────────────────────────────────────────
  const loadVehicles = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchAllVehicles();
      setVehicles(data);
      setFiltersInitialized(false);
    } catch (err) {
      console.error(err);
      setLoadError('Failed to load inventory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVehicles(); }, []);

  // Listen for real-time updates from the import modal
  useEffect(() => {
    const handler = () => { loadVehicles(); };
    window.addEventListener('pm_vehicles_updated', handler);
    return () => window.removeEventListener('pm_vehicles_updated', handler);
  }, []);

  const dynamicPriceMin = vehicles.length > 0 ? Math.min(...vehicles.map((v) => v.price)) : 0;
  const dynamicPriceMax = vehicles.length > 0 ? Math.max(...vehicles.map((v) => v.price)) : 100000;
  const dynamicYearMin = vehicles.length > 0 ? Math.min(...vehicles.map((v) => v.year)) : 2000;
  const dynamicYearMax = vehicles.length > 0 ? Math.max(...vehicles.map((v) => v.year)) : new Date().getFullYear();
  const dynamicMileageMin = 0;
  const dynamicMileageMax = vehicles.length > 0 ? Math.max(...vehicles.map((v) => v.mileage)) : 300000;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (selectedVehicle || mobileFiltersOpen) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [selectedVehicle, mobileFiltersOpen]);

  useEffect(() => {
    if (!filtersInitialized && vehicles.length > 0) {
      setPriceMin(dynamicPriceMin); setPriceMax(dynamicPriceMax);
      setYearMin(dynamicYearMin); setYearMax(dynamicYearMax);
      setMileageMin(dynamicMileageMin); setMileageMax(dynamicMileageMax);
      setFiltersInitialized(true);
    }
  }, [vehicles, filtersInitialized]);

  useEffect(() => { setCurrentPage(1); }, [selectedBrand, selectedModel, selectedType, selectedFuel, selectedCondition, priceMin, priceMax, yearMin, yearMax, mileageMin, mileageMax, sortBy, searchQuery]);

  // Reset model when brand changes
  useEffect(() => { setSelectedModel('All Models'); }, [selectedBrand]);

  const handlePriceMinChange = (e: React.ChangeEvent<HTMLInputElement>) => setPriceMin(Math.min(Number(e.target.value), priceMax - 500));
  const handlePriceMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => setPriceMax(Math.max(Number(e.target.value), priceMin + 500));
  const handleYearMinChange = (e: React.ChangeEvent<HTMLInputElement>) => setYearMin(Math.min(Number(e.target.value), yearMax - 1));
  const handleYearMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => setYearMax(Math.max(Number(e.target.value), yearMin + 1));
  const handleMileageMinChange = (e: React.ChangeEvent<HTMLInputElement>) => setMileageMin(Math.min(Number(e.target.value), mileageMax - 1000));
  const handleMileageMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => setMileageMax(Math.max(Number(e.target.value), mileageMin + 1000));

  const activeFilterCount = [
    selectedBrand !== 'All Brands', selectedModel !== 'All Models', selectedType !== 'All Types', selectedFuel !== 'All Fuel',
    selectedCondition !== 'All',
    priceMin !== dynamicPriceMin || priceMax !== dynamicPriceMax,
    yearMin !== dynamicYearMin || yearMax !== dynamicYearMax,
    mileageMin !== dynamicMileageMin || mileageMax !== dynamicMileageMax,
  ].filter(Boolean).length;

  const resetAllFilters = () => {
    setSelectedBrand('All Brands'); setSelectedModel('All Models'); setSelectedType('All Types');
    setSelectedFuel('All Fuel'); setSelectedCondition('All');
    setPriceMin(dynamicPriceMin); setPriceMax(dynamicPriceMax);
    setYearMin(dynamicYearMin); setYearMax(dynamicYearMax);
    setMileageMin(dynamicMileageMin); setMileageMax(dynamicMileageMax);
  };

  // Derive available models based on selected brand
  const availableModels = (() => {
    const brandFiltered = selectedBrand === 'All Brands' ? vehicles : vehicles.filter((v) => v.brand === selectedBrand);
    const modelSet = new Set(
      brandFiltered
        .map((v) => v.model?.trim())
        .filter(Boolean)
    );
    return ['All Models', ...Array.from(modelSet).sort()];
  })();

  // Normalize fuel strings so "Gas" and "Gasoline" are treated the same
  const normalizeFuel = (fuel: string): string => {
    const f = (fuel ?? '').toLowerCase().trim();
    if (f === 'gasoline' || f === 'gas') return 'gas';
    return f;
  };

  const applyFilters = (list: Vehicle[]) =>
    list
      .filter((v) => selectedBrand === 'All Brands' || (v.brand ?? '').toLowerCase() === selectedBrand.toLowerCase())
      .filter((v) => selectedModel === 'All Models' || (v.model ?? '').trim().toLowerCase() === selectedModel.toLowerCase())
      .filter((v) => selectedType === 'All Types' || (v.type ?? '').toLowerCase() === selectedType.toLowerCase())
      .filter((v) => selectedFuel === 'All Fuel' || normalizeFuel(v.fuel) === normalizeFuel(selectedFuel))
      .filter((v) => selectedCondition === 'All' || (v.condition ?? '').toLowerCase() === selectedCondition.toLowerCase())
      .filter((v) => v.price >= priceMin && v.price <= priceMax)
      .filter((v) => v.year >= yearMin && v.year <= yearMax)
      .filter((v) => v.mileage >= mileageMin && v.mileage <= mileageMax)
      .filter((v) => searchQuery === '' ||
        (v.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (v.brand ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (v.model ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => {
        if (sortBy === 'price-low') return a.price - b.price;
        if (sortBy === 'price-high') return b.price - a.price;
        if (sortBy === 'year') return b.year - a.year;
        return 0;
      });

  const availableVehicles = vehicles.filter((v) => !v.sold);
  const soldVehicles = vehicles.filter((v) => v.sold);
  const filteredAvailable = applyFilters(availableVehicles);
  const filteredSold = applyFilters(soldVehicles);

  const totalPages = filteredAvailable.length >= VEHICLES_PER_PAGE ? Math.ceil(filteredAvailable.length / VEHICLES_PER_PAGE) : 1;
  const paginatedAvailable = filteredAvailable.length >= VEHICLES_PER_PAGE
    ? filteredAvailable.slice((currentPage - 1) * VEHICLES_PER_PAGE, currentPage * VEHICLES_PER_PAGE)
    : filteredAvailable;
  const showPagination = filteredAvailable.length >= VEHICLES_PER_PAGE;

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  const formatMileage = (mileage: number) => mileage === 0 ? 'New' : `${mileage.toLocaleString()} mi`;
  const proxyImage = (url: string) => `https://images.weserv.nl/?url=${encodeURIComponent(url)}&w=800&output=webp`;

  const getGalleryImages = (vehicle: Vehicle): string[] => {
    if (vehicle.images && vehicle.images.length > 0) return vehicle.images;
    return [vehicle.image];
  };

  const handleImport = () => {
    setShowImportModal(false);
    loadVehicles();
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Inventory', path: '/inventory' },
    { name: 'Financing', path: '/financing' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const getVehicleTitle = (vehicle: Vehicle): string => {
    const modelPart = vehicle.name.replace(new RegExp(`^${vehicle.brand}\\s*`, 'i'), '').trim();
    return `${vehicle.year} ${vehicle.brand} ${modelPart}`.trim();
  };

  const minPricePercent = dynamicPriceMax > dynamicPriceMin ? ((priceMin - dynamicPriceMin) / (dynamicPriceMax - dynamicPriceMin)) * 100 : 0;
  const maxPricePercent = dynamicPriceMax > dynamicPriceMin ? ((priceMax - dynamicPriceMin) / (dynamicPriceMax - dynamicPriceMin)) * 100 : 100;
  const minYearPercent = dynamicYearMax > dynamicYearMin ? ((yearMin - dynamicYearMin) / (dynamicYearMax - dynamicYearMin)) * 100 : 0;
  const maxYearPercent = dynamicYearMax > dynamicYearMin ? ((yearMax - dynamicYearMin) / (dynamicYearMax - dynamicYearMin)) * 100 : 100;
  const minMileagePercent = dynamicMileageMax > 0 ? (mileageMin / dynamicMileageMax) * 100 : 0;
  const maxMileagePercent = dynamicMileageMax > 0 ? (mileageMax / dynamicMileageMax) * 100 : 100;

  useEffect(() => {
    if (!selectedVehicle) return;
    const handleKey = (e: KeyboardEvent) => {
      const imgs = getGalleryImages(selectedVehicle);
      if (e.key === 'ArrowLeft') setSelectedImageIndex((p) => (p === 0 ? imgs.length - 1 : p - 1));
      if (e.key === 'ArrowRight') setSelectedImageIndex((p) => (p === imgs.length - 1 ? 0 : p + 1));
      if (e.key === 'Escape') setSelectedVehicle(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedVehicle, selectedImageIndex]);

  const RangeTrack = ({ minPct, maxPct, minVal, maxVal, minInput, maxInput }: {
    minPct: number; maxPct: number; minVal: string; maxVal: string;
    minInput: React.ReactNode; maxInput: React.ReactNode;
  }) => (
    <>
      <div className="flex justify-between mb-2">
        <span className="text-xs font-bold" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>{minVal}</span>
        <span className="text-xs font-bold" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>{maxVal}</span>
      </div>
      <div className="relative h-5 flex items-center mb-1">
        <div className="absolute w-full h-1 bg-white/10 rounded-full"></div>
        <div className="absolute h-1 bg-[#C9A84C] rounded-full" style={{ left: `${minPct}%`, width: `${maxPct - minPct}%` }}></div>
        {minInput}
        {maxInput}
      </div>
    </>
  );

  const FilterSection = ({ title, sectionKey, children }: { title: string; sectionKey: keyof typeof openSections; children: React.ReactNode }) => (
    <div className="border-b border-white/5 pb-4 mb-4">
      <button onClick={() => toggleSection(sectionKey)} className="flex items-center justify-between w-full mb-3 cursor-pointer group">
        <span className="text-[10px] font-bold tracking-[0.2em] uppercase group-hover:text-white/90 transition-colors" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>{title}</span>
        {(sectionKey === 'brand' || sectionKey === 'model') ? (
          <i className={`fa-solid ${openSections[sectionKey] ? 'fa-minus' : 'fa-plus'} text-xs transition-all`} style={{ color: '#C9A84C' }}></i>
        ) : (
          <i className={`ri-arrow-${openSections[sectionKey] ? 'up' : 'down'}-s-line text-sm transition-transform`} style={{ color: '#fffffff3' }}></i>
        )}
      </button>
      {openSections[sectionKey] && <div>{children}</div>}
    </div>
  );

  const SidebarFilters = () => (
    <aside className="hidden lg:flex flex-col w-64 xl:w-72 flex-shrink-0 self-start sticky top-28">
      <div className="bg-[#111111] border border-[#C9A84C]/30 rounded-sm overflow-hidden shadow-[0_0_30px_rgba(201,168,76,0.08)] ring-1 ring-[#C9A84C]/10">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#C9A84C]/20 bg-[#C9A84C]/5">
          <div className="flex items-center gap-2">
            <i className="ri-equalizer-line text-[#C9A84C] text-base"></i>
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-[#C9A84C] text-[#0d0d0d] text-[10px] font-black px-1.5 py-0.5 rounded-full leading-none">{activeFilterCount}</span>
            )}
          </div>
          {activeFilterCount > 0 && (
            <button onClick={resetAllFilters} className="text-[10px] text-[#C9A84C]/60 hover:text-[#C9A84C] transition-colors cursor-pointer tracking-wider uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Clear All</button>
          )}
        </div>
        <div className="px-5 py-5 space-y-0 max-h-[calc(100vh-180px)] overflow-y-auto sidebar-scroll">
          <div className="mb-5">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#fffffff3' }}></i>
              <input type="text" placeholder="Search vehicles..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2.5 bg-[#1a1a1a] border border-white/10 focus:border-[#C9A84C] rounded-sm text-xs placeholder-white/30 focus:outline-none transition-all" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }} />
            </div>
          </div>
          <FilterSection title="Make" sectionKey="brand">
            <div className="flex flex-col gap-1.5">
              {brands.map((b) => (
                <button key={b} onClick={() => setSelectedBrand(b)} className={`flex items-center justify-between px-3 py-2 rounded-sm text-xs transition-all cursor-pointer text-left ${selectedBrand === b ? 'bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#C9A84C]' : 'hover:bg-white/5 border border-transparent'}`} style={{ fontFamily: 'Montserrat, sans-serif', color: selectedBrand === b ? undefined : '#fffffff3' }}>
                  <span>{b}</span>
                  {selectedBrand === b && <i className="ri-check-line text-[#C9A84C] text-xs"></i>}
                </button>
              ))}
            </div>
          </FilterSection>
          <FilterSection title="Model" sectionKey="model">
            <div className="flex flex-col gap-1.5">
              {availableModels.map((m) => (
                <button key={m} onClick={() => setSelectedModel(m)} className={`flex items-center justify-between px-3 py-2 rounded-sm text-xs transition-all cursor-pointer text-left ${selectedModel === m ? 'bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#C9A84C]' : 'hover:bg-white/5 border border-transparent'}`} style={{ fontFamily: 'Montserrat, sans-serif', color: selectedModel === m ? undefined : '#fffffff3' }}>
                  <span>{m}</span>
                  {selectedModel === m && <i className="ri-check-line text-[#C9A84C] text-xs"></i>}
                </button>
              ))}
            </div>
          </FilterSection>
          <FilterSection title="Body Type" sectionKey="type">
            <div className="flex flex-wrap gap-1.5">
              {types.map((t) => (
                <button key={t} onClick={() => setSelectedType(t)} className={`px-3 py-1.5 rounded-sm text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${selectedType === t ? 'bg-[#C9A84C] text-[#0d0d0d]' : 'bg-white/5 hover:bg-white/10 border border-white/10'}`} style={{ fontFamily: 'Montserrat, sans-serif', color: selectedType === t ? undefined : '#fffffff3' }}>{t}</button>
              ))}
            </div>
          </FilterSection>
          <FilterSection title="Condition" sectionKey="condition">
            <div className="flex gap-2">
              {conditions.map((c) => (
                <button key={c} onClick={() => setSelectedCondition(c)} className={`flex-1 py-2 rounded-sm text-[10px] font-bold tracking-wider uppercase transition-all cursor-pointer ${selectedCondition === c ? 'bg-[#C9A84C] text-[#0d0d0d]' : 'bg-white/5 hover:bg-white/10 border border-white/10'}`} style={{ fontFamily: 'Montserrat, sans-serif', color: selectedCondition === c ? undefined : '#fffffff3' }}>{c}</button>
              ))}
            </div>
          </FilterSection>
          <FilterSection title="Fuel Type" sectionKey="fuel">
            <div className="flex flex-wrap gap-1.5">
              {fuels.map((f) => (
                <button key={f} onClick={() => setSelectedFuel(f)} className={`px-3 py-1.5 rounded-sm text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${selectedFuel === f ? 'bg-[#C9A84C] text-[#0d0d0d]' : 'bg-white/5 hover:bg-white/10 border border-white/10'}`} style={{ fontFamily: 'Montserrat, sans-serif', color: selectedFuel === f ? undefined : '#fffffff3' }}>{f}</button>
              ))}
            </div>
          </FilterSection>
          <FilterSection title="Price Range" sectionKey="price">
            <div className="px-1">
              <RangeTrack minPct={minPricePercent} maxPct={maxPricePercent} minVal={formatPrice(priceMin)} maxVal={formatPrice(priceMax)}
                minInput={<input type="range" min={dynamicPriceMin} max={dynamicPriceMax} step={500} value={priceMin} onChange={handlePriceMinChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: priceMin > dynamicPriceMax - 5000 ? 5 : 3 }} />}
                maxInput={<input type="range" min={dynamicPriceMin} max={dynamicPriceMax} step={500} value={priceMax} onChange={handlePriceMaxChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: 4 }} />}
              />
              <div className="flex justify-between mt-1">
                <span className="text-[10px]" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>{formatPrice(dynamicPriceMin)}</span>
                <span className="text-[10px]" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>{formatPrice(dynamicPriceMax)}</span>
              </div>
            </div>
          </FilterSection>
          <FilterSection title="Year Range" sectionKey="year">
            <div className="px-1">
              <RangeTrack minPct={minYearPercent} maxPct={maxYearPercent} minVal={String(yearMin)} maxVal={String(yearMax)}
                minInput={<input type="range" min={dynamicYearMin} max={dynamicYearMax} step={1} value={yearMin} onChange={handleYearMinChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: yearMin > dynamicYearMax - 2 ? 5 : 3 }} />}
                maxInput={<input type="range" min={dynamicYearMin} max={dynamicYearMax} step={1} value={yearMax} onChange={handleYearMaxChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: 4 }} />}
              />
              <div className="flex justify-between mt-1">
                <span className="text-[10px]" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>{dynamicYearMin}</span>
                <span className="text-[10px]" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>{dynamicYearMax}</span>
              </div>
            </div>
          </FilterSection>
          <FilterSection title="Mileage" sectionKey="mileage">
            <div className="px-1">
              <RangeTrack minPct={minMileagePercent} maxPct={maxMileagePercent} minVal={`${(mileageMin / 1000).toFixed(0)}K mi`} maxVal={`${(mileageMax / 1000).toFixed(0)}K mi`}
                minInput={<input type="range" min={dynamicMileageMin} max={dynamicMileageMax} step={1000} value={mileageMin} onChange={handleMileageMinChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: mileageMin > dynamicMileageMax - 10000 ? 5 : 3 }} />}
                maxInput={<input type="range" min={dynamicMileageMin} max={dynamicMileageMax} step={1000} value={mileageMax} onChange={handleMileageMaxChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: 4 }} />}
              />
              <div className="flex justify-between mt-1">
                <span className="text-[10px]" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>0</span>
                <span className="text-[10px]" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>{(dynamicMileageMax / 1000).toFixed(0)}K+</span>
              </div>
            </div>
          </FilterSection>
        </div>
        <div className="px-5 py-4 border-t border-[#C9A84C]/20 bg-[#C9A84C]/5">
          <p className="text-[10px] text-center" style={{ fontFamily: 'Montserrat, sans-serif', color: '#fffffff3' }}>
            <span className="text-[#C9A84C] font-bold">{filteredAvailable.length}</span> vehicle{filteredAvailable.length !== 1 ? 's' : ''} available
          </p>
        </div>
      </div>
    </aside>
  );

  const handleShare = (vehicle: Vehicle) => {
    const url = `${window.location.origin}/inventory?vehicle=${encodeURIComponent(vehicle.id)}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    }
  };

  // Schema.org JSON-LD injection
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://paramountautosusa.com';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          "@id": `${baseUrl}/inventory#itemlist`,
          "url": `${baseUrl}/inventory`,
          "name": "Paramount Motors Vehicle Inventory",
          "description": "Browse our complete inventory of luxury and exotic pre-owned vehicles. Premium cars, SUVs, trucks, and high-performance vehicles available with nationwide shipping.",
          "numberOfItems": vehicles.length,
          "itemListElement": vehicles.slice(0, 10).map((vehicle, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Car",
              "name": `${vehicle.year} ${vehicle.brand} ${vehicle.name}`,
              "brand": {
                "@type": "Brand",
                "name": vehicle.brand
              },
              "model": vehicle.model || vehicle.name,
              "vehicleModelDate": vehicle.year,
              "mileageFromOdometer": {
                "@type": "QuantitativeValue",
                "value": vehicle.mileage,
                "unitCode": "SMI"
              },
              "fuelType": vehicle.fuel,
              "vehicleTransmission": vehicle.transmission || "Automatic",
              "offers": {
                "@type": "Offer",
                "price": vehicle.price,
                "priceCurrency": "USD",
                "availability": vehicle.sold ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
                "seller": {
                  "@id": `${baseUrl}/#organization`
                }
              },
              "image": vehicle.image
            }
          }))
        },
        {
          "@type": "AutoDealer",
          "@id": `${baseUrl}/#organization`,
          "name": "Paramount Motors",
          "url": baseUrl,
          "logo": "https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png"
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [vehicles]);

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      <title>Inventory | Paramount Autos USA – Used Cars for Sale Miami, FL</title>
      <meta name="description" content="Browse our full inventory of used cars, trucks, and SUVs at Paramount Autos USA in Miami, FL. Luxury vehicles, work trucks, and family SUVs available with nationwide shipping." />
      <link rel="canonical" href="https://paramountautosusa.com/inventory" />

      {/* Navigation */}
      <nav className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#0d0d0d]/95 backdrop-blur-md shadow-lg shadow-black/30' : 'bg-[#0d0d0d]'}`}>
        <div className="w-full px-4 sm:px-8 lg:px-16 py-2">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png"
                alt="Paramount Motors Logo"
                style={{ height: '5rem', width: 'auto' }}
                className="object-contain"
              />
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <Link key={item.name} to={item.path} className={`text-xs font-medium tracking-widest uppercase transition-all cursor-pointer relative ${item.name === 'Inventory' ? 'text-[#C9A84C]' : 'text-white/60 hover:text-[#C9A84C]'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {item.name}
                  {item.name === 'Inventory' && <span className="absolute -bottom-1 left-0 w-full h-px bg-[#C9A84C]"></span>}
                </Link>
              ))}
            </div>
            <Link to="/financing" className="hidden lg:block px-6 py-2.5 rounded-sm text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] hover:bg-[#e0bc5e] transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform" style={{ fontFamily: 'Montserrat, sans-serif' }}>Financing</Link>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer flex-shrink-0">
              <i className={`ri-close-line ${isMobileMenuOpen ? 'ri-close-line' : 'fa-solid fa-bars'} text-2xl text-[#C9A84C]`}></i>
            </button>
          </div>
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-3 pb-4 border-t border-white/10 pt-4">
              <div className="flex flex-col gap-4">
                {navItems.map((item) => (
                  <Link key={item.name} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className="text-xs font-medium tracking-widest uppercase transition-colors cursor-pointer text-[#C9A84C] hover:text-[#e0bc5e]" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.name}</Link>
                ))}
                <Link to="/financing" onClick={() => setIsMobileMenuOpen(false)} className="mt-1 px-6 py-3 rounded-sm text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] cursor-pointer whitespace-nowrap text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>Financing</Link>
              </div>
            </div>
          )}
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A84C]/50 to-transparent"></div>
        <AnnouncementBar />
      </nav>

      {/* Hero Banner */}
      <section className="relative h-48 sm:h-64 lg:h-72 flex items-end justify-start overflow-hidden pt-28 sm:pt-32">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/9d8744d3e090360d408ce3e133614920.jpg" alt="Paramount Motors Inventory" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/90 via-[#0d0d0d]/60 to-[#0d0d0d]/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 px-4 sm:px-8 lg:px-16 pb-4 sm:pb-6 lg:pb-8 w-full">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <div className="h-px w-6 sm:w-8 bg-[#C9A84C]"></div>
            <span className="text-[#C9A84C] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Full Collection</span>
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Our Inventory</h1>
          <p className="text-white/50 text-xs sm:text-sm mt-1" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
            {loading ? 'Loading...' : `${availableVehicles.length} vehicles available`}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="px-4 sm:px-8 lg:px-10 xl:px-16 py-6 lg:py-10 pb-24 lg:pb-16">
        <div className="flex gap-6 xl:gap-8 items-start">

          <SidebarFilters />

          <div className="flex-1 min-w-0">

            {/* Top bar */}
            <div className="hidden lg:flex items-center justify-between mb-6 pb-4 border-b border-white/5">
              <p className="text-xs text-white/60" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {loading ? (
                  <span className="text-white/40">Loading inventory...</span>
                ) : (
                  <>Showing <span className="text-white font-semibold">{filteredAvailable.length}</span> available vehicle{filteredAvailable.length !== 1 ? 's' : ''}
                  {filteredSold.length > 0 && <span className="text-white/40"> · {filteredSold.length} sold</span>}</>
                )}
              </p>
              <div className="flex items-center gap-3">
                {isEditMode && (
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border border-[#C9A84C]/30 hover:border-[#C9A84C]/70 hover:bg-[#C9A84C]/10 text-[#C9A84C] rounded-sm text-[10px] font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <i className="ri-file-excel-2-line text-sm"></i>
                    Import Excel
                  </button>
                )}
                <span className="text-[10px] text-white/50 tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Sort by</span>
                <div className="relative">
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="appearance-none bg-[#1a1a1a] border border-white/10 hover:border-[#C9A84C]/50 px-4 py-2 pr-8 rounded-sm text-xs font-medium text-white/80 cursor-pointer focus:outline-none focus:border-[#C9A84C] transition-all" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="year">Newest First</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none text-xs"></i>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-32 gap-4">
                <div className="w-12 h-12 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin"></div>
                <p className="text-white/40 text-xs tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Loading inventory...</p>
              </div>
            )}

            {/* Error State */}
            {!loading && loadError && (
              <div className="flex flex-col items-center justify-center py-24 gap-4">
                <div className="w-14 h-14 flex items-center justify-center border border-red-700/40 rounded-sm">
                  <i className="ri-error-warning-line text-2xl text-red-400"></i>
                </div>
                <p className="text-red-400 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>{loadError}</p>
                <button onClick={loadVehicles} className="px-6 py-2.5 bg-[#C9A84C] text-[#0d0d0d] rounded-sm text-[10px] font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap" style={{ fontFamily: 'Montserrat, sans-serif' }}>Retry</button>
              </div>
            )}

            {/* Vehicle Grid */}
            {!loading && !loadError && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5" data-product-shop>
                  {paginatedAvailable.map((vehicle, index) => (
                    <div key={vehicle.id} className="group bg-[#141414] border border-[#C9A84C]/25 hover:border-[#C9A84C]/70 rounded-sm overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-[#C9A84C]/15 animate-fadeInUp" style={{ animationDelay: `${index * 40}ms` }}>
                      <div className="cursor-pointer" onClick={() => { setSelectedVehicle(vehicle); setSelectedImageIndex(0); }}>
                        <div className="relative w-full h-48 overflow-hidden">
                          <img src={proxyImage(vehicle.image)} alt={vehicle.name} className="w-full h-full object-cover scale-[1.12] group-hover:scale-[1.22] transition-transform duration-700" />
                          <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                            <span className={`px-2.5 sm:px-3 py-1 text-[10px] sm:text-xs font-bold tracking-wider uppercase ${vehicle.condition === 'New' ? 'bg-[#C9A84C] text-[#0d0d0d]' : 'bg-white/10 text-white backdrop-blur-sm'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>{vehicle.condition}</span>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="mb-2">
                            <h3 className="text-sm font-semibold text-white leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>{getVehicleTitle(vehicle)}</h3>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5 mb-4">
                            {[
                              { icon: 'ri-speed-line', label: formatMileage(vehicle.mileage) },
                              { icon: 'ri-settings-3-line', label: vehicle.transmission ?? 'Automatic' },
                              { icon: 'ri-gas-station-line', label: vehicle.fuel },
                            ].map((tag) => (
                              <div key={tag.label} className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-sm text-[10px] text-white/75" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                <i className={tag.icon}></i>
                                <span>{tag.label}</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t border-white/5">
                            <p className="text-base font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '-0.02em' }}>{formatPrice(vehicle.price)}</p>
                            <div className="bg-[#C9A84C]/10 hover:bg-[#C9A84C] text-[#C9A84C] hover:text-[#0d0d0d] px-3 py-2 rounded-sm text-[10px] font-bold tracking-wider uppercase transition-all cursor-pointer whitespace-nowrap" style={{ fontFamily: 'Montserrat, sans-serif' }}>View Details</div>
                          </div>
                        </div>
                      </div>
                      <div className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
                        <PaymentCalculator vehiclePrice={vehicle.price} isCompact={true} />
                        <Link to={`/financing?vehicle=${encodeURIComponent(getVehicleTitle(vehicle))}`} className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 bg-[#C9A84C]/10 hover:bg-[#C9A84C] border border-[#C9A84C]/40 hover:border-[#C9A84C] text-[#C9A84C] hover:text-[#0d0d0d] rounded-sm text-[10px] font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap hover:scale-[1.02] transform text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>Apply for Financing</Link>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {showPagination && (
                  <div className="flex items-center justify-center gap-2 mt-10 mb-4">
                    <button onClick={() => { setCurrentPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={currentPage === 1} className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[#C9A84C]/60 text-white/50 hover:text-[#C9A84C] rounded-sm transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-left-s-line text-base"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      const isActive = page === currentPage;
                      const isNearCurrent = Math.abs(page - currentPage) <= 2;
                      const isEdge = page === 1 || page === totalPages;
                      if (!isNearCurrent && !isEdge) {
                        if (page === 2 || page === totalPages - 1) return <span key={page} className="text-white/30 text-xs px-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>…</span>;
                        return null;
                      }
                      return (
                        <button key={page} onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className={`w-9 h-9 flex items-center justify-center rounded-sm text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${isActive ? 'bg-[#C9A84C] text-[#0d0d0d] border border-[#C9A84C]' : 'border border-white/10 hover:border-[#C9A84C]/60 text-white/50 hover:text-[#C9A84C]'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>{page}</button>
                      );
                    })}
                    <button onClick={() => { setCurrentPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={currentPage === totalPages} className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[#C9A84C]/60 text-white/50 hover:text-[#C9A84C] rounded-sm transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-right-s-line text-base"></i>
                    </button>
                  </div>
                )}
                {showPagination && (
                  <p className="text-center text-[10px] text-white/30 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Page {currentPage} of {totalPages} · {filteredAvailable.length} vehicles</p>
                )}

                {!loading && filteredAvailable.length === 0 && filteredSold.length === 0 && vehicles.length > 0 && (
                  <div className="text-center py-24">
                    <div className="w-16 h-16 flex items-center justify-center border border-white/10 rounded-sm mx-auto mb-5">
                      <i className="ri-car-line text-3xl text-white/20"></i>
                    </div>
                    <h3 className="text-xl font-semibold text-white/60 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>No vehicles found</h3>
                    <p className="text-white/30 text-sm mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>Try adjusting your filters to see more results</p>
                    <button onClick={resetAllFilters} className="px-6 py-2.5 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-[1.02] transform" style={{ fontFamily: 'Montserrat, sans-serif' }}>Clear All Filters</button>
                  </div>
                )}

                {!loading && vehicles.length === 0 && !loadError && (
                  <div className="text-center py-24">
                    <div className="w-16 h-16 flex items-center justify-center border border-white/10 rounded-sm mx-auto mb-5">
                      <i className="ri-car-line text-3xl text-white/20"></i>
                    </div>
                    <h3 className="text-xl font-semibold text-white/60 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>No inventory yet</h3>
                    <p className="text-white/30 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>Import your vehicles using the Import Excel button above.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0d0d0d]/95 backdrop-blur-md border-t border-[#C9A84C]/20 px-4 py-3 flex items-center gap-3">
        <button onClick={() => setMobileFiltersOpen(true)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[48px] rounded-md text-sm font-bold tracking-wider uppercase border transition-all cursor-pointer ${activeFilterCount > 0 ? 'bg-[#C9A84C] text-[#0d0d0d] border-[#C9A84C]' : 'bg-[#1a1a1a] text-white/70 border-white/10'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <i className="ri-equalizer-line text-xl"></i>
          Filters
          {activeFilterCount > 0 && <span className="bg-[#0d0d0d] text-[#C9A84C] text-xs font-black px-2 py-0.5 rounded-full">{activeFilterCount}</span>}
        </button>
        <div className="relative flex-1">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full appearance-none bg-[#1a1a1a] border border-white/10 px-4 py-3 min-h-[48px] pr-10 rounded-md text-base text-white/70 cursor-pointer focus:outline-none focus:border-[#C9A84C] transition-all" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <option value="featured">Featured</option>
            <option value="price-low">Price ↑</option>
            <option value="price-high">Price ↓</option>
            <option value="year">Newest</option>
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none text-base"></i>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {mobileFiltersOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-fadeIn" onClick={() => setMobileFiltersOpen(false)}></div>
          <div className="lg:hidden fixed inset-x-0 bottom-0 z-50 bg-[#0d0d0d] rounded-t-2xl max-h-[85vh] flex flex-col animate-slideUp shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center gap-3">
                <i className="ri-equalizer-line text-[#C9A84C] text-xl"></i>
                <h3 className="text-base font-bold text-white tracking-wider uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Filters</h3>
                {activeFilterCount > 0 && <span className="bg-[#C9A84C] text-[#0d0d0d] text-xs font-black px-2 py-0.5 rounded-full">{activeFilterCount}</span>}
              </div>
              <button onClick={() => setMobileFiltersOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                <i className="ri-close-line text-white text-xl"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 space-y-5">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-base"></i>
                <input type="text" placeholder="Search vehicles..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-11 pr-4 py-3 min-h-[48px] bg-[#1a1a1a] border border-white/10 focus:border-[#C9A84C] rounded-md text-base text-white placeholder-white/30 focus:outline-none transition-all" style={{ fontFamily: 'Montserrat, sans-serif' }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/40 mb-2 tracking-wider uppercase font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Make</label>
                  <div className="relative">
                    <select value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)} className="w-full appearance-none bg-[#1a1a1a] border border-white/10 px-4 py-3 min-h-[48px] pr-10 rounded-md text-base text-white/70 cursor-pointer focus:outline-none focus:border-[#C9A84C] transition-all" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none text-base"></i>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-2 tracking-wider uppercase font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Model</label>
                  <div className="relative">
                    <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} className="w-full appearance-none bg-[#1a1a1a] border border-white/10 px-4 py-3 min-h-[48px] pr-10 rounded-md text-base text-white/70 cursor-pointer focus:outline-none focus:border-[#C9A84C] transition-all" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {availableModels.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none text-base"></i>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/40 mb-2 tracking-wider uppercase font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Type</label>
                  <div className="relative">
                    <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="w-full appearance-none bg-[#1a1a1a] border border-white/10 px-4 py-3 min-h-[48px] pr-10 rounded-md text-base text-white/70 cursor-pointer focus:outline-none focus:border-[#C9A84C] transition-all" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {types.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none text-base"></i>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-2 tracking-wider uppercase font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Condition</label>
                  <div className="relative">
                    <select value={selectedCondition} onChange={(e) => setSelectedCondition(e.target.value)} className="w-full appearance-none bg-[#1a1a1a] border border-white/10 px-4 py-3 min-h-[48px] pr-10 rounded-md text-base text-white/70 cursor-pointer focus:outline-none focus:border-[#C9A84C] transition-all" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {conditions.map((c) => <option key={c} value={c}>{c === 'All' ? 'All Conditions' : c}</option>)}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none text-base"></i>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-3 tracking-wider uppercase font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Price Range</label>
                <div className="bg-[#1a1a1a] border border-[#C9A84C]/20 rounded-sm p-4">
                  <RangeTrack minPct={minPricePercent} maxPct={maxPricePercent} minVal={formatPrice(priceMin)} maxVal={formatPrice(priceMax)}
                    minInput={<input type="range" min={dynamicPriceMin} max={dynamicPriceMax} step={500} value={priceMin} onChange={handlePriceMinChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: priceMin > dynamicPriceMax - 5000 ? 5 : 3 }} />}
                    maxInput={<input type="range" min={dynamicPriceMin} max={dynamicPriceMax} step={500} value={priceMax} onChange={handlePriceMaxChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: 4 }} />}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-3 tracking-wider uppercase font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Year Range</label>
                <div className="bg-[#1a1a1a] border border-[#C9A84C]/20 rounded-sm p-4">
                  <RangeTrack minPct={minYearPercent} maxPct={maxYearPercent} minVal={String(yearMin)} maxVal={String(yearMax)}
                    minInput={<input type="range" min={dynamicYearMin} max={dynamicYearMax} step={1} value={yearMin} onChange={handleYearMinChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: yearMin > dynamicYearMax - 2 ? 5 : 3 }} />}
                    maxInput={<input type="range" min={dynamicYearMin} max={dynamicYearMax} step={1} value={yearMax} onChange={handleYearMaxChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: 4 }} />}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-3 tracking-wider uppercase font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Mileage Range</label>
                <div className="bg-[#1a1a1a] border border-[#C9A84C]/20 rounded-sm p-4">
                  <RangeTrack minPct={minMileagePercent} maxPct={maxMileagePercent} minVal={`${(mileageMin / 1000).toFixed(0)}K mi`} maxVal={`${(mileageMax / 1000).toFixed(0)}K mi`}
                    minInput={<input type="range" min={dynamicMileageMin} max={dynamicMileageMax} step={1000} value={mileageMin} onChange={handleMileageMinChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: mileageMin > dynamicMileageMax - 10000 ? 5 : 3 }} />}
                    maxInput={<input type="range" min={dynamicMileageMin} max={dynamicMileageMax} step={1000} value={mileageMax} onChange={handleMileageMaxChange} className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer range-input" style={{ zIndex: 4 }} />}
                  />
                </div>
              </div>
              {activeFilterCount > 0 && (
                <button onClick={resetAllFilters} className="w-full py-3 rounded-md text-sm font-bold tracking-widest uppercase border border-white/20 text-white/60 hover:border-[#C9A84C]/50 hover:text-[#C9A84C] transition-all cursor-pointer" style={{ fontFamily: 'Montserrat, sans-serif' }}>Clear All Filters</button>
              )}
            </div>
            <div className="flex-shrink-0 px-5 py-4 border-t border-white/10 bg-[#0d0d0d]">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-white/60" style={{ fontFamily: 'Montserrat, sans-serif' }}>{filteredAvailable.length} vehicle{filteredAvailable.length !== 1 ? 's' : ''} available</span>
              </div>
              <button onClick={() => setMobileFiltersOpen(false)} className="w-full py-3.5 min-h-[52px] sm:min-h-[48px] bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] rounded-md text-sm font-bold tracking-widest uppercase cursor-pointer transition-all shadow-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Show {filteredAvailable.length} Result{filteredAvailable.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Vehicle Detail Modal */}
      {selectedVehicle && (() => {
        const galleryImages = getGalleryImages(selectedVehicle);
        const hasMultiple = galleryImages.length > 1;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fadeIn" onClick={() => setSelectedVehicle(null)}>
            <div className="relative bg-[#141414] border-0 sm:border border-white/10 rounded-none sm:rounded-sm w-full h-full sm:max-w-4xl sm:w-full sm:max-h-[90vh] sm:h-auto overflow-y-auto overscroll-contain modal-scroll animate-slideUp" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setSelectedVehicle(null)} className="absolute top-4 right-4 z-30 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-[#C9A84C] border border-white/20 hover:border-[#C9A84C] text-white hover:text-[#0d0d0d] rounded-sm transition-all cursor-pointer backdrop-blur-sm shadow-lg">
                <i className="fa-solid fa-xmark text-xl"></i>
              </button>
              <div className="relative w-full flex-shrink-0">
                <div className="relative w-full h-64 sm:h-96 overflow-hidden bg-[#0d0d0d]">
                  <img key={selectedImageIndex} src={proxyImage(galleryImages[selectedImageIndex])} alt={`${selectedVehicle.name} - photo ${selectedImageIndex + 1}`} className="w-full h-full object-cover object-center scale-[1.12] animate-galleryFade" />
                  <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                    <span className={`px-2.5 sm:px-3 py-1 text-[10px] sm:text-xs font-bold tracking-wider uppercase ${selectedVehicle.condition === 'New' ? 'bg-[#C9A84C] text-[#0d0d0d]' : 'bg-white/10 text-white backdrop-blur-sm'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>{selectedVehicle.condition}</span>
                  </div>
                  {hasMultiple && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedImageIndex((p) => (p === 0 ? galleryImages.length - 1 : p - 1)); }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-[#C9A84C] border border-white/20 hover:border-[#C9A84C] text-white hover:text-[#0d0d0d] rounded-sm transition-all cursor-pointer backdrop-blur-sm shadow-lg"
                      >
                        <i className="fa-solid fa-chevron-left text-sm"></i>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedImageIndex((p) => (p === galleryImages.length - 1 ? 0 : p + 1)); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-[#C9A84C] border border-white/20 hover:border-[#C9A84C] text-white hover:text-[#0d0d0d] rounded-sm transition-all cursor-pointer backdrop-blur-sm shadow-lg"
                      >
                        <i className="fa-solid fa-chevron-right text-sm"></i>
                      </button>
                      <div className="absolute bottom-3 right-3 z-10 bg-black/60 backdrop-blur-sm text-white/70 text-[10px] font-bold px-2.5 py-1 rounded-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {selectedImageIndex + 1} / {galleryImages.length}
                      </div>
                    </>
                  )}
                </div>
                {/* Thumbnail Strip */}
                {hasMultiple && (
                  <div className="flex gap-2 px-3 py-3 bg-[#0d0d0d] overflow-x-auto scrollbar-hide">
                    {galleryImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={(e) => { e.stopPropagation(); setSelectedImageIndex(idx); }}
                        className={`flex-shrink-0 w-16 h-12 sm:w-20 sm:h-14 rounded-sm overflow-hidden border-2 transition-all cursor-pointer ${selectedImageIndex === idx ? 'border-[#C9A84C] opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`}
                      >
                        <img src={proxyImage(img)} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover object-center" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-5 sm:p-8">
                <div className="mb-5 sm:mb-6 p-3 sm:p-4 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-sm">
                  <div className="flex items-start gap-2 sm:gap-3 mb-2">
                    <i className="ri-truck-line text-[#C9A84C] text-lg sm:text-xl"></i>
                    <div>
                      <p className="text-white text-xs sm:text-sm font-semibold mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Nationwide Shipping Available</p>
                      <p className="text-white/50 text-xs sm:text-sm" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>We ship to all 48 contiguous states. Contact us for a shipping quote.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 sm:gap-3 pt-2 border-t border-[#C9A84C]/20">
                    <i className="ri-shield-check-line text-[#C9A84C] text-lg sm:text-xl mt-0.5"></i>
                    <div>
                      <p className="text-white text-xs sm:text-sm font-semibold mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Extended Warranty Available</p>
                      <p className="text-white/50 text-xs sm:text-sm" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>Ask us about warranty options when you apply for financing.</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start justify-between mb-5 sm:mb-6 gap-3 sm:gap-0">
                  <div>
                    <p className="text-[#C9A84C] text-[10px] sm:text-xs tracking-widest uppercase mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>{selectedVehicle.brand}</p>
                    <h2 className="text-xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>{getVehicleTitle(selectedVehicle)}</h2>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '-0.02em' }}>{formatPrice(selectedVehicle.price)}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-6">
                  {[
                    { icon: 'ri-speed-line', label: 'Mileage', value: formatMileage(selectedVehicle.mileage) },
                    { icon: 'ri-dashboard-3-line', label: 'Horsepower', value: selectedVehicle.horsepower ? `${selectedVehicle.horsepower} HP` : '—' },
                    { icon: 'ri-settings-3-line', label: 'Transmission', value: selectedVehicle.transmission ?? '—' },
                    { icon: 'ri-gas-station-line', label: 'Fuel Type', value: selectedVehicle.fuel ?? '—' },
                  ].map((item) => (
                    <div key={item.label} className="flex flex-col gap-1 bg-[#1a1a1a] border border-white/10 px-3 sm:px-4 py-2.5 sm:py-3 rounded-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <span className="text-[9px] sm:text-[10px] text-white/40 tracking-widest uppercase">{item.label}</span>
                      <div className="flex items-center gap-1.5">
                        <i className={`${item.icon} text-[#C9A84C] text-xs`}></i>
                        <span className="text-white text-xs sm:text-sm font-semibold">{item.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {[
                    { label: 'Exterior', value: selectedVehicle.color?.split(' / ')[0] ?? '—', icon: 'ri-palette-line' },
                    { label: 'Interior', value: selectedVehicle.color?.split(' / ')[1] ?? '—', icon: 'ri-brush-line' },
                    { label: 'Engine', value: selectedVehicle.engine ?? '—', icon: 'ri-settings-2-line' },
                    { label: 'VIN', value: selectedVehicle.vin ?? '—', icon: 'ri-barcode-line' },
                  ].map((spec) => (
                    <div key={spec.label} className="flex flex-col gap-1 bg-[#1a1a1a] border border-white/10 px-3 sm:px-4 py-2.5 sm:py-3 rounded-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <span className="text-[9px] sm:text-[10px] text-white/40 tracking-widest uppercase">{spec.label}</span>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <i className={`${spec.icon} text-[#C9A84C] text-xs`}></i>
                        <span className="text-white text-xs sm:text-sm font-semibold truncate">{spec.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-xs sm:text-sm font-bold tracking-widest uppercase text-white/70 mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Key Features</h3>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {selectedVehicle.features.map((feature, idx) => (
                      <span key={idx} className="bg-[#C9A84C]/10 border border-[#C9A84C]/20 text-[#C9A84C] px-3 sm:px-4 py-1.5 sm:py-2 rounded-sm text-[10px] sm:text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>{feature}</span>
                    ))}
                  </div>
                </div>
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-xs sm:text-sm font-bold tracking-widest uppercase text-white/70 mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Payment Calculator</h3>
                  <PaymentCalculator vehiclePrice={selectedVehicle.price} isCompact={false} />
                </div>
                <div className="flex flex-col gap-3">
                  <Link to={`/financing?vehicle=${encodeURIComponent(getVehicleTitle(selectedVehicle))}`} className="w-full bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] py-4 sm:py-3.5 min-h-[52px] sm:min-h-[48px] rounded-sm text-sm sm:text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-[1.02] transform text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>Apply for Financing</Link>
                  <Link to="/contact" className="w-full border border-[#C9A84C]/40 hover:border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C]/10 py-4 sm:py-3.5 min-h-[52px] sm:min-h-[48px] rounded-sm text-sm sm:text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-[1.02] transform text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>Contact Us</Link>
                  <button
                    onClick={() => handleShare(selectedVehicle)}
                    className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 text-white/50 hover:text-white/80 py-4 sm:py-3.5 min-h-[52px] sm:min-h-[48px] rounded-sm text-sm sm:text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:bg-white/5"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <i className={`${shareCopied ? 'fa-solid fa-check text-[#C9A84C]' : 'fa-solid fa-share-nodes'} text-sm`}></i>
                    <span className={shareCopied ? 'text-[#C9A84C]' : ''}>{shareCopied ? 'Link Copied!' : 'Share This Vehicle'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Footer */}
      <footer className="bg-[#080808] border-t border-white/5 py-8 sm:py-12 px-4 sm:px-8 lg:px-16">
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent mb-8 sm:mb-12"></div>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center gap-6 sm:gap-8 text-center">
            <div className="flex items-center gap-2 sm:gap-3">
              <img
                src="https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png"
                alt="Paramount Motors Logo"
                style={{ height: '3rem', width: 'auto' }}
                className="object-contain"
              />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              {navItems.map((item) => (
                <Link key={item.name} to={item.path} className="text-xs text-white/60 hover:text-[#C9A84C] transition-colors cursor-pointer tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.name}</Link>
              ))}
            </div>
            <div className="flex items-center justify-center gap-3">
              {['ri-facebook-fill', 'ri-instagram-line', 'ri-twitter-x-line'].map((icon) => (
                <a key={icon} href="#" className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[#C9A84C] text-white/60 hover:text-[#C9A84C] rounded-sm transition-all cursor-pointer"><i className={`${icon} text-sm`}></i></a>
              ))}
            </div>
          </div>
          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-white/5 text-center">
            <p className="text-xs text-white/40" style={{ fontFamily: 'Montserrat, sans-serif' }}>© 2025 Paramount Motors. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Import Excel Modal — editor only */}
      {showImportModal && (
        <ImportExcelModal
          onClose={() => setShowImportModal(false)}
          onImport={handleImport}
          existingCount={vehicles.length}
        />
      )}

      {/* Share Toast */}
      <div className={`fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2.5 px-5 py-3 bg-[#1a1a1a] border border-[#C9A84C]/50 rounded-sm shadow-2xl shadow-black/50 transition-all duration-300 whitespace-nowrap ${shareCopied ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="fa-solid fa-check text-[#C9A84C] text-sm"></i>
        </div>
        <span className="text-white text-xs font-semibold tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>Vehicle link copied to clipboard!</span>
      </div>

      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes galleryFade { from { opacity: 0; transform: scale(1.02); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeInUp { animation: fadeInUp 0.5s ease-out forwards; }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .animate-slideUp { animation: slideUp 0.4s ease-out forwards; }
        .animate-galleryFade { animation: galleryFade 0.35s ease-out forwards; }
        .sidebar-scroll::-webkit-scrollbar { width: 3px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: #C9A84C30; border-radius: 2px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: #C9A84C60; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .range-input::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 16px; height: 16px; border-radius: 50%;
          background: #C9A84C; border: 2px solid #0d0d0d;
          cursor: pointer; box-shadow: 0 0 0 2px #C9A84C40; transition: box-shadow 0.2s;
        }
        .range-input::-webkit-slider-thumb:hover { box-shadow: 0 0 0 4px #C9A84C40; }
        .range-input::-moz-range-thumb {
          width: 16px; height: 16px; border-radius: 50%;
          background: #C9A84C; border: 2px solid #0d0d0d; cursor: pointer;
        }
      `}</style>
    </div>
  );
}
